import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/useAuth";
import axios from "axios";
import {
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import { API_URL } from "../../config/config";
import { WarehouseData } from "./warehouses.types";

const setNestedValue = (obj: any, path: string, value: any): any => {
  const keys = path.split(".");
  const lastKey = keys.pop()!;
  const newObj = { ...obj };
  let cur = newObj;
  for (const key of keys) {
    if (cur[key] === undefined) cur[key] = {};
    cur = cur[key];
  }
  cur[lastKey] = value;
  return newObj;
};

const UpdateWarehouse: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<WarehouseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    const fetchStore = async () => {
      try {
        setLoading(true);
        if (!user) return;
        const res = await fetch(`${API_URL}/warehouse/${user.id}/${id}/`);
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        setFormData(data.data);
      } catch (err: any) {
        console.error(err);
        setError(err.message || "Failed to fetch store");
      } finally {
        setLoading(false);
      }
    };

    if (!isLoading) {
      fetchStore();
    }
  }, [user, isLoading, id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      if (!prev) return prev;
      // If nested (contains a dot), update nested prop, else top-level
      if (name.includes(".")) {
        return setNestedValue(prev, name, value);
      } else {
        return { ...prev, [name]: value };
      }
    });
  };

  const getValue = (path: string) => {
    if (!formData) return "";
    const keys = path.split(".");
    let val: any = formData;
    for (const key of keys) {
      val = val?.[key];
      if (val === undefined || val === null) return "";
    }
    return val;
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleSubmit = async () => {
    if (!formData || !user) return;
    try {
      setSaving(true);
      const updatedData: any = new FormData();

      // Add only relevant fields manually
      updatedData.append("name", formData.name);
      updatedData.append("address", formData.id_BusinessLocation.address);
      updatedData.append("phone_number", formData.phone_number);
      updatedData.append("email", formData.email);
      updatedData.append("capacity", formData.capacity.toString());
      updatedData.append("latitude", formData.latitude.toString());
      updatedData.append("longitude", formData.longitude.toString());
      updatedData.append("region", formData.region);
      updatedData.append("city", formData.city);
      updatedData.append("store_code", formData.store_code);
      updatedData.append("status", formData.status);

      // ✅ Only keep mon-fri, sat, sun in the opening_hours object
      const compactOpeningHours = {
        "mon-fri": formData.opening_hours!["mon-fri"],
        sat: formData.opening_hours!.sat,
        sun: formData.opening_hours!.sun,
      };

      updatedData.append("opening_hours", JSON.stringify(compactOpeningHours));

      if (selectedFile) {
        updatedData.append("warehouse_image", selectedFile);
      }

      await axios.put(
        `${API_URL}/warehouse/${user.id}/${formData.id}`,
        updatedData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      navigate(`/warehouse/`);
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!formData) return <Typography>No warehouse data available.</Typography>;

  return (
    <Box sx={{ p: 3, maxWidth: 600, mx: "auto" }}>
      <Typography variant="h5" mb={2}>
        Update Warehouse {formData.name}
      </Typography>

      <TextField
        fullWidth
        margin="normal"
        label="Name"
        name="name"
        value={formData.name ?? "N/A"}
        onChange={handleChange}
      />

      <TextField
        fullWidth
        margin="normal"
        label="Address"
        name="id_BusinessLocation.address"
        value={formData.id_BusinessLocation?.address ?? "N/A"}
        onChange={handleChange}
      />

      <TextField
        fullWidth
        margin="normal"
        label="Phone Number"
        name="phone_number"
        value={formData.phone_number ?? "N/A"}
        onChange={handleChange}
      />

      <TextField
        fullWidth
        margin="normal"
        label="Email"
        name="email"
        value={formData.email ?? "N/A"}
        onChange={handleChange}
      />

      <TextField
        fullWidth
        margin="normal"
        label="Capacity"
        name="capacity"
        type="number"
        value={formData.capacity ?? "N/A"}
        onChange={handleChange}
      />

      <TextField
        fullWidth
        margin="normal"
        label="Latitude"
        name="latitude"
        type="number"
        value={formData.latitude ?? "N/A"}
        onChange={handleChange}
      />

      <TextField
        fullWidth
        margin="normal"
        label="Longitude"
        name="longitude"
        type="number"
        value={formData.longitude ?? "N/A"}
        onChange={handleChange}
      />

      <TextField
        fullWidth
        margin="normal"
        label="Region"
        name="region"
        value={formData.region ?? "N/A"}
        onChange={handleChange}
      />

      <TextField
        fullWidth
        margin="normal"
        label="City"
        name="city"
        value={formData.city ?? "N/A"}
        onChange={handleChange}
      />

      <TextField
        fullWidth
        margin="normal"
        label="Warehouse Code"
        name="wh_code"
        value={formData.store_code ?? "N/A"}
        onChange={handleChange}
      />

      <TextField
        fullWidth
        margin="normal"
        label="Status"
        name="status"
        value={formData.status ?? "N/A"}
        onChange={handleChange}
      />

      {/* Opening Hours could be handled more elegantly with time pickers or dynamic fields */}
      <TextField
        fullWidth
        margin="normal"
        label="Opening Hours (Mon-Fri)"
        name="opening_hours.mon-fri"
        value={getValue("opening_hours.mon-fri")}
        onChange={handleChange}
      />

      <TextField
        fullWidth
        margin="normal"
        label="Opening Hours (Sat)"
        name="opening_hours.sat"
        value={getValue("opening_hours.sat")}
        onChange={handleChange}
      />

      <TextField
        fullWidth
        margin="normal"
        label="Opening Hours (Sun)"
        name="opening_hours.sun"
        value={getValue("opening_hours.sun")}
        onChange={handleChange}
      />

      <Button variant="contained" component="label" sx={{ mt: 2 }}>
        Upload New Image
        <input
          type="file"
          hidden
          onChange={handleImageChange}
          accept="image/*"
        />
      </Button>

      <Box mt={3}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? "Saving..." : "Update Store"}
        </Button>
      </Box>
    </Box>
  );
};

export default UpdateWarehouse;
